<template>
  <div id="database-page-wrapper">
    <!-- Show loader if axios request has not completed yet -->
    <LoaderComponent v-if="!loaded" />
    <!-- Once axios request is successful, display page content -->
    <div v-else>
      <!-- Language options bar at the top of content section -->
      <div id="languages">
        <button
          class="languages-button"
          :class="{
            'language-selected': language.rel === currentLanguage,
          }"
          v-for="language in languages"
          :key="language.href"
          @click="setLanguage(language.rel)"
        >
          {{ languageNames[language.rel] }}
        </button>
      </div>

      <!-- Glossary "windows" -->
      <!-- 
      Each word has one window, on click the matching window
      is displayed, positioned absolutely according to click
    -->
      <div
        class="glossary-entry-container"
        v-for="word in glossary"
        :key="word.name"
        :ref="word.name"
        :dir="rightToLeft ? 'rtl' : 'ltr'"
      >
        <div
          class="glossary-close"
          v-if="showGlossary[word.name]"
          @click="hideGlossary(word.name)"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </div>
        <div
          class="glossary-header"
          v-if="showGlossary[word.name]"
          :style="{ 'background-image': `url(${getWorldImage()})` }"
        >
          {{ $t("glossary") }}
        </div>
        <div class="glossary-entry" v-if="showGlossary[word.name]">
          <div class="glossary-word">{{ currentWord }}</div>
          <div class="glossary-definition" v-html="word.definition"></div>
        </div>
      </div>

      <div id="database-object-container">
        <BackComponent />

        <div id="database-object-wrapper">
          <div id="photo-info-wrapper">
            <!-- Photo(s) and source links -->
            <!-- Includes lightbox that opens upon clicking photo -->
            <div id="photo-container">
              <div class="photo" @click="showLightbox = true">
                <!-- Component for zooming photo on hover -->
                <ZoomComponent :img-normal="currentPhoto"></ZoomComponent>
              </div>
              <div id="thumbnail-container">
                <!-- Create thumbnail for every photo available -->
                <div
                  class="thumbnail"
                  v-for="pic in photoData"
                  :key="pic.imagePath"
                  @click="currentPhoto = pic.imagePath, currentPhotoCaption = pic.caption"
                >
                  <img :src="pic.imagePath" />
                  <!-- Tooltip info displays on hover of thumbnail -->
                  <div
                    class="tooltip-text"
                    v-if="(pic.photographer || pic.copyright) && !lessThan974"
                  >
                    <div v-if="pic.photographer">
                      {{ $t("photograph") }}: {{ pic.photographer }}
                    </div>
                    <div v-if="pic.copyright">&#169; {{ pic.copyright }}</div>
                  </div>
                </div>
              </div>
              <!-- Whole-page lightbox for photo slideshow/full-size viewing -->
              <div
                id="lightbox-container"
                v-if="showLightbox"
                @click="showLightbox = false"
              >
                <div id="lightbox">
                  <img :src="currentPhoto" />
                </div>
                <!-- Slideshow controls -->
                <div
                  id="lightbox-left"
                  class="lightbox-control"
                  v-if="photoData.length > 1"
                >
                  <button @click.stop="slideshow('left')">
                    <font-awesome-icon icon="fa-solid fa-chevron-left" />
                  </button>
                </div>
                <div
                  id="lightbox-right"
                  class="lightbox-control"
                  v-if="photoData.length > 1"
                >
                  <button @click.stop="slideshow('right')">
                    <font-awesome-icon icon="fa-solid fa-chevron-right" />
                  </button>
                </div>
              </div>
              <div class="current-caption" v-if="currentPhotoCaption" v-html="currentPhotoCaption"></div>
              <!-- Source & Add to Collection links -->
              <div
                id="links-container"
                :class="{ 'fas-rtl': rightToLeft }"
                :dir="rightToLeft ? 'rtl' : 'ltr'"
              >
                <p id="source-link">
                  <!-- If source project is not current project, open in a different window, change FA icon -->
                  <a v-if="project === dxa" :href="sourceDatabase">
                    <font-awesome-icon :icon="['fas', 'share']" /> {{ $t("source") }} <span>({{ sourceDatabaseDomain }})</span>
                  </a>
                  <a v-else :href="sourceDatabase" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> <bdi
                      >{{ $t("source") }}
                      <span>({{ sourceDatabaseDomain }})</span></bdi
                    >
                  </a>
                </p>
                <p id="add-collection-link">
                  <a :href="addToCollection" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> {{ $t("addToCollection") }}
                  </a>
                </p>
              </div>
            </div>

            <!-- Main object information -->
            <!-- Includes RTL if language is Arabic -->
            <!-- Hide labels if there is no information available -->
            <div
              id="info-container"
              :class="{ 'fas-rtl': rightToLeft }"
              :dir="rightToLeft ? 'rtl' : 'ltr'"
            >
              <div
                id="info-eiac"
                v-if="showEIACmessage"
                v-html="$t('note_EIAC') + ' ' + this.availableLanguages"
              ></div>
              <div id="info-copyedit" v-if="copyedit" v-html="copyedit"></div>
              <!-- Data format: { key: [i18n label, string] } -->
              <div v-for="(data, key) in filteredObjectData" :key="key">
                <!-- Add toggle for Short Description when available -->
                <p
                  class="info-label"
                  :class="{
                    'info-label-short':
                      !showShortDesc && key === 'shortDescription',
                    pointer: key === 'shortDescription',
                  }"
                  @click="toggleShortDesc(key)"
                  v-html="data[0]"></p>
                <!-- Style partner museum profile link -->
                <!-- Ref for description(s) to add Glossary links -->
                <p
                  class="info"
                  :class="{
                    'info-link': key === 'museum' || key === 'catalogue',
                  }"
                  v-show="key !== 'shortDescription' || showShortDesc"
                  v-html="data[1]"
                  :ref="key"
                ></p>
              </div>
              <div id="info-copyright" v-if="copyright">
                <p class="info-label">{{ $t("objCopyrightInfo") }}</p>
                <p class="info">{{ copyright }}</p>
              </div>
              <div>
                <p class="info-label">{{ $t("citation") }}</p>
                <!-- Author(s) “Title” in Online Museum Title, Museum With No Frontiers, year of copying, URL -->
                <p id="info-citation">
                  <span v-if="filteredCitationData.prepared"
                    >{{ filteredCitationData.prepared[1].string }}
                  </span>
                  "<span v-html="filteredObjectData.name[1]"></span>"
                  {{ $t("in") }}
                  <span id="info-project-name">{{ projectName }}</span
                  >, Museum With No Frontiers, {{ new Date().getFullYear() }}.
                  <span id="info-citation-source">{{ sourceDatabase }}</span>
                </p>
                <div
                  class="info-authors"
                  v-for="(data, key) in filteredCitationData"
                  :key="key"
                >
                  <span class="info-authors-label">{{ data[0] }} </span>
                  <div
                    class="info-authors-name"
                    :class="{ 'cv-hover': author.curriculum }"
                    v-for="(author, index) in data[1].info"
                    :key="author.name"
                    @click.stop="
                      (authorHover = author.name),
                        (authorType = key),
                        positionHover($event), closePopups(false)">
                    {{ author.name}}
                    <span
                      class="info-authors-comma"
                      v-if="
                        data[1].info.length > 1 &&
                        index !== data[1].info.length - 1">,</span>
                    <div class="info-authors-cv-close" 
                      v-show="
                        author.curriculum &&
                        authorHover === author.name &&
                        key === authorType"
                      @click.stop="(authorHover = null), (authorType = null)">
                      <font-awesome-icon :icon="['fas', 'xmark']" />
                    </div>
                    <div
                      class="info-authors-cv"
                      v-show="
                        author.curriculum &&
                        authorHover === author.name &&
                        key === authorType"
                      v-html="author.curriculum"
                    ></div>
                  </div>
                </div>
                <p class="info-working-number" v-if="workingNumber[1]">
                  {{ workingNumber[0] }} {{ workingNumber[1] }}
                </p>
              </div>
              <!-- Conditional class for applying source project color -->
              <div id="info-citation-link" :class="backgroundColor(project, isExhibition)">
                <p>{{ $t("source") }}</p>
                <!-- If source database link matches current page, do not open in a new tab -->
                <p v-if="currentURL === sourceDatabase">
                  <a :href="sourceDatabase">{{ sourceDatabase }}</a>
                </p>
                <p v-else>
                  <a :href="sourceDatabase" target="_blank">{{
                    sourceDatabase
                  }}</a>
                </p>
              </div>
            </div>
          </div>

          <!-- Related Content section -->
          <div
            id="related-content-container"
            :class="{ 'fas-rtl': rightToLeft }"
            :dir="rightToLeft ? 'rtl' : 'ltr'"
          >
            <p class="related-header">
              {{ $t("relatedContent").toUpperCase() }}
            </p>
            <p id="related-description" class="related-margin">
              {{ $t("relatedContentDescription") }}
            </p>

            <!-- Related objects (thumbnails) -->
            <div
              id="related-objects-container"
              v-if="
                checkExists(relatedObjects) || checkExists(relatedObjectsAlt)
              "
            >
              <div class="related-margin">{{ $t("relatedObjects") }}</div>
              <div id="related-objects-wrapper" class="related-margin">
                <div
                  class="related-objects"
                  v-for="object in editedRelatedObjects"
                  :key="object.link"
                >
                  <a :href="object['remote-link']" target="_blank"
                    ><img :src="object.image"
                  /></a>
                  <div class="related-tooltip" v-if="!lessThan974">
                    <div><span v-html="object.name"></span>, {{ object.country }}</div>
                    <div>{{ object.justification }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Artistic Introduction link -->
            <div
              id="art-intro-container"
              v-if="project === 'ISL' || project === 'EPM'"
            >
              <a
                id="art-intro-link"
                :href="urls.islamicArt + '/gai/ISL/'"
                target="_blank"
              >
                <p
                  id="art-intro"
                  class="related-margin"
                  :class="{
                    'conditional-margin':
                      checkExists(relatedObjects) ||
                      checkExists(relatedObjectsAlt),
                  }"
                >
                  <font-awesome-icon icon="fa-solid fa-up-right-from-square" /> 
                  {{ $t("artisticIntroduction") }}
                </p>
              </a>
            </div>

            <!-- Timeline window -->
            <div id="timeline-container" v-if="checkExists(timeline)">
              <p
                id="timeline"
                class="related-margin"
                :class="{
                  'conditional-margin': project === 'ISL' || project === 'EPM',
                }"
                @click="showPopup('showTimeline')"
              >
                <font-awesome-icon :icon="['fas', 'share']" /> {{ $t("timeline") }}
              </p>
              <!-- Exclude from RTL, because information is always in English -->
              <div
                id="timeline-popout"
                ref="timelinePopout"
                v-if="showTimeline"
                dir="ltr"
              >
                <div id="timeline-popout-close" @click="showTimeline = false">
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </div>
                <div
                  id="timeline-popout-title"
                  :style="{ 'background-image': `url(${getWorldImage()})` }"
                >
                  Timeline
                </div>
                <div id="timeline-popout-option">
                  <div>
                    <label for="timeline-select">See events for:</label>
                    <select
                      id="timeline-select"
                      v-model="timelineInfo.country"
                      @change="
                        getTimelineEvents(
                          timelineInfo.country,
                          timelineInfo.start,
                          timelineInfo.end
                        )
                      "
                    >
                      <option
                        v-for="country in timelineCountries"
                        :key="country[0]"
                        :value="country[0]"
                      >
                        {{ country[1] }}
                      </option>
                    </select>
                  </div>
                  <div id="timeline-popout-link">
                    <router-link
                      :to="{
                        path: '/timeline-results',
                        query: {
                          c: timelineInfo.country,
                          start: timelineInfo.startRounded,
                          end: timelineInfo.endRounded,
                        },
                      }"
                    >
                      <font-awesome-icon :icon="['fas', 'share']" /> Click here to begin a full
                      Timeline search
                    </router-link>
                  </div>
                </div>
                <div id="timeline-popout-scroll">
                  <div id="timeline-scroll-subheader">
                    <div>
                      {{ timeline[Object.keys(timeline)[0]]["countryName"] }},
                      {{ timelineInfo.start }} - {{ timelineInfo.end }}
                      <span v-if="Math.sign(timelineInfo.start) === -1">
                        B.C.</span
                      >
                      <span v-else> A.D.</span>
                    </div>
                  </div>
                  <div id="timeline-scroll-text">
                    <div
                      class="timeline-event"
                      v-for="event in timeline"
                      :key="event.countryId + event.eventId"
                    >
                      <div class="timeline-date">
                        {{ event.A }}
                        <span v-if="Math.sign(event.A) === -1"> B.C.</span>
                        <span v-else> A.D.</span>
                      </div>
                      <div v-html="event.description"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Glossary Tool section -->
            <div id="glossary-tool-container">
              <p
                id="glossary-tool"
                :class="{ 'conditional-margin': !checkExists(timeline) }"
                @click="showPopup('showGlossaryTool')"
              >
                <font-awesome-icon :icon="['fas', 'share']" /> {{ $t("glossary") }}
              </p>
              <div
                id="glossary-tool-popout"
                v-if="showGlossaryTool"
                @click="showWordList = false"
              >
                <div
                  id="glossary-tool-popout-close"
                  @click="showGlossaryTool = false"
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </div>
                <div
                  id="glossary-tool-popout-title"
                  :style="{ 'background-image': `url(${getWorldImage()})` }"
                >
                  {{ $t("glossary") }}
                </div>
                <div id="glossary-tool-instructions">
                  {{ $t("glossaryInstructions") }}
                </div>
                <div id="glossary-tool-input">
                  <input
                    v-model="glossaryInput"
                    @click="selectText()"
                    @keyup="
                      initiateGlossarySearch(
                        currentLanguage,
                        glossaryInput
                      )
                    "
                    type="text"
                    ref="glossaryInput"
                  />
                  <div id="glossary-tool-list" v-if="showWordList">
                    <ul>
                      <li
                        v-for="(word, index) in sortedGlossaryResults"
                        :key="word"
                        @click="selectWord(word)"
                        @mouseover="(wordHover = true), (wordIndex = index)"
                        @mouseleave="(wordHover = false), (wordIndex = null)"
                        :class="{
                          'glossary-hover': wordHover && index === wordIndex,
                        }"
                      >
                        {{ word }}
                      </li>
                    </ul>
                  </div>
                </div>
                <div id="glossary-tool-scroll" v-if="selectedDefinition !== ''">
                  <div id="glossary-tool-text">
                    <p id="glossary-tool-definition-label">
                      {{ $t("definition") }}
                    </p>
                    <p
                      id="glossary-tool-definition"
                      v-html="selectedDefinition"
                    ></p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Dynasty section -->
            <div id="dynasty-container" v-if="checkExists(filteredDynastyData)">
              <p class="conditional-margin">{{ $t("islamicDynasties") }}</p>
              <div
                class="dynasty-wrapper"
                v-for="(dynasty, index) in filteredDynastyData"
                :key="dynasty.name"
              >
                <p class="dynasty" @click="showPopup('showDynasty', index)">
                  <font-awesome-icon :icon="['fas', 'share']" /> {{ dynasty.name }}
                </p>
                <div id="dynasty-popout" v-if="index === showDynasty">
                  <div id="dynasty-popout-close" @click="showDynasty = null">
                    <font-awesome-icon :icon="['fas', 'xmark']" />
                  </div>
                  <div
                    id="dynasty-popout-title"
                    :style="{ 'background-image': `url(${getWorldImage()})` }"
                  >
                    {{ $t("theDynasties") }}
                  </div>
                  <div id="dynasty-popout-scroll">
                    <div id="dynasty-name">{{ dynasty.name }}</div>
                    <div id="dynasty-text">
                      <p v-if="dynasty.alsoKnownAs">
                        {{ dynasty.alsoKnownAs }}
                      </p>
                      <p class="dynasty-area" v-if="dynasty.area" v-html="dynasty.area"></p>
                      <p v-if="dynasty.dateDescription">
                        {{ dynasty.dateDescription }}
                      </p>
                      <p v-html="dynasty.history"></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Audio/Video section -->
            <div
              id="media-container"
              v-if="checkExists(audio) || checkExists(video)"
            >
              <div id="audio-wrapper" v-if="checkExists(audio)">
                <p class="conditional-margin">{{ $t("relatedAudio") }}</p>
                <div
                  class="audio-links-wrapper"
                  v-for="file in audio"
                  :key="file.url"
                >
                  <a class="audio-link" :href="file.url" target="_blank">
                    <p class="audio">
                      <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> {{ file.title }}
                    </p>
                    <div class="related-tooltip" v-if="!lessThan974">
                      <div>{{ file.description }}</div>
                    </div>
                  </a>
                </div>
              </div>
              <div id="video-wrapper" v-if="checkExists(video)">
                <p class="conditional-margin">{{ $t("relatedVideos") }}</p>
                <div
                  class="video-links-wrapper"
                  v-for="file in video"
                  :key="file.url"
                >
                  <a class="video-link" :href="file.url" target="_blank">
                    <p class="video">
                      <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> {{ file.title }}
                    </p>
                    <div
                      class="related-tooltip"
                      v-if="!lessThan974 && file.description"
                    >
                      <div>{{ file.description }}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- Other Related Content sections -->
            <div>
              <p class="related-header">{{ $t("onDisplayIn") }}</p>
              <div
                id="exhibitions"
                class="related-margin"
                v-if="showExhibitions"
              >
                <p>
                  <bdi class="bold">{{ $t("exhibitions") }}</bdi>
                </p>
                <p id="exhibition-project">{{ exhibitionData[0].project }}</p>
                <p
                  class="exhibit"
                  v-for="exhibit in exhibitionData"
                  :key="exhibit.href"
                >
                  <a :href="exhibit.href" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> 
                    {{ exhibit.exhibition }} |
                    <span v-html="exhibit.theme"></span>
                    <span v-if="exhibit.subTheme"><span> | </span>{{ exhibit.subTheme }}</span>
                  </a>
                </p>
              </div>
              <div id="new-exhibitions" v-if="allExhibitions && allExhibitions.length > 0">
                <p v-if="!showExhibitions" class="bold">{{ $t("exhibitions") }}</p>
                <div class="new-exhibition-link" v-for="(theme, index) in allExhibitions" :key="`${theme.ParentThemeTitle}+${theme.ItemId}`">
                  <p class="italic new-exhibition-title" v-if="index === 0 || theme.ExhibitionTitle !== allExhibitions[index - 1].ExhibitionTitle"
                    :class="{ 'first-exhibition-title': index === 0 }">
                    {{ theme.ExhibitionTitle }}
                  </p>
                  <!-- <router-link v-if="theme.ThemeNumber == 0" :to="{ name: 'about' }">
                    <p>
                      <font-awesome-icon icon="fa-solid fa-share" />{{ theme.ThemeTitle }}
                    </p>
                  </router-link>
                  <router-link v-else :to="{
                    name: 'theme',
                    params: {
                      id: theme.ParentThemeDisplayOrder - 1,
                      subtheme: theme.SubThemeNumber ? theme.SubThemeNumber : theme.ThemeTitle.toLowerCase(),
                      image: theme.ItemId
                    }
                  }">
                    <p>
                      <font-awesome-icon icon="fa-solid fa-share" />{{ theme.ParentThemeTitle }} | <span class="italic">{{ theme.ThemeTitle }}</span>
                    </p>
                  </router-link> -->
                  <a :href="`${urls.exhibition}/${theme.Folder}/${theme.LanguageId}/theme/${theme.ParentThemeDisplayOrder - 1}/${theme.SubThemeNumber ? theme.SubThemeNumber : theme.ThemeTitle.toLowerCase()}/${theme.ItemId}`"
                    target="_blank">
                    <p>
                      <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> {{ theme.ParentThemeTitle }} | <span class="italic">{{ theme.ThemeTitle }}</span>
                    </p>
                  </a>
                </div>
              </div>
              <div id="galleries">
                <p class="bold">{{ $t("galleries") }}</p>
                <p v-for="gallery in galleryData" :key="gallery.href">
                  <a :href="gallery.href" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> {{ gallery.name }}
                  </a>
                </p>
              </div>
            </div>
            <!-- Related Database Search Links -->
            <div>
              <div
                v-if="
                  project === 'ISL' ||
                  project === 'EPM' ||
                  project === 'AWE' ||
                  project === 'DBA'
                "
              >
                <p class="related-header">{{ $t("searchRelatedDatabase") }}</p>
                <p v-if="project === 'ISL' || project === 'EPM'">
                  <a :href="urls.islamicArt + '/database.php'" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> Discover Islamic Art
                  </a>
                </p>
                <p v-if="project === 'DBA'">
                  <a :href="urls.baroqueArt + '/database.php'" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> Discover Baroque Art
                  </a>
                </p>
                <p v-if="project === 'AWE'">
                  <a :href="urls.sharingHistory + '/database.php'">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> Sharing History
                  </a>
                </p>
              </div>
              <p class="related-header">{{ $t("goToFullSearch") }}</p>
              <a
                :href="urls.portal + '/database_searchform.php'"
                target="_blank"
              >
                <font-awesome-icon icon="fa-solid fa-up-right-from-square" /> 
                {{ $t("overallDatabase") }}
              </a>
            </div>
            <!-- Travel Book Link -->
            <div v-if="travelBookLink">
              <p class="related-header">{{ $t("seeAlso") }}</p>
              <div>
                <p> 
                  <a :href="travelBookLink" target="_blank">
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']"/> {{
                    $t("travelBook")
                  }}</a>
                </p>
              </div>
            </div>
            <!-- Download -->
            <div>
              <p class="related-header">
                <font-awesome-icon :icon="['fas', 'download']" /> {{ $t("download") }}
              </p>
              <p class="download" @click="printPage()">
                <font-awesome-icon :icon="['fas', 'share']" /> {{ $t("asPDF") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>



<script>
// Internationalization
import { loadLocaleMessages } from "../i18n.js";
import i18n from "../i18n.js";
import BackComponent from "./BackComponent.vue";
import LoaderComponent from "./LoaderComponent.vue";
// Component for zooming an image on hover
import ZoomComponent from "./ZoomComponent.vue";
import media from "../mixins/media.js";
// Contains functions for Timeline information
import timeline from "../mixins/timeline.js";
export default {
  name: "DatabaseItem",
  mixins: [media, timeline],
  components: {
    BackComponent: BackComponent,
    LoaderComponent: LoaderComponent,
    ZoomComponent: ZoomComponent,
  },
  // Create i18n for this component and copy in loaded messages/translations from global
  i18n: { 
    messages: i18n.global.messages,
    sync: false,
  },
  data() {
    return {
      dxa: process.env.VUE_APP_DXA,
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      loaded: false,
      // Link to retrieve object information
      link: "",
      languages: {},
      languageNames: {},
      currentLanguage: "",
      rightToLeft: null,
      photoData: {},
      currentPhoto: "",
      currentCaption: "",
      showLightbox: false,
      sourceDatabase: "",
      sourceDatabaseDomain: "",
      addToCollection: "",
      project: "",
      isExhibition: null,
      showEIACmessage: false,
      copyedit: null,
      glossary: [],
      showGlossary: {},
      currentWord: "",
      // Format: { key: [i18n key, data] }
      // Displays with v-html using data
      objectData: {},
      bothDescsExist: null,
      showShortDesc: null,
      copyright: null,
      projectName: "",
      citationData: {},
      workingNumber: [],
      relatedObjects: [],
      // Holds relatedObjects data in main language
      // (For display when there is no info in selected language)
      relatedObjectsAlt: [],
      timelineInfo: {
        country: "",
        start: "",
        startRounded: "",
        end: "",
        endRounded: "",
      },
      timelineCountries: [],
      years: [],
      timeline: [],
      showTimeline: false,
      glossaryResults: {},
      glossaryTimeout: null,
      glossaryInput: "",
      showWordList: false,
      wordHover: false,
      wordIndex: null,
      selectedDefinition: "",
      showGlossaryTool: false,
      dynasties: [],
      showDynasty: null,
      audio: [],
      video: [],
      galleryData: [],
      exhibitionData: [],
      allExhibitions: [],
      showExhibitions: false,
      travelBookLink: "",
      authorHover: null,
      authorType: null,
      urls: {
        portal: process.env.VUE_APP_PORTAL_URL,
        baroqueArt: process.env.VUE_APP_BAROQUE_ART_URL,
        islamicArt: process.env.VUE_APP_ISLAMIC_ART_URL,
        sharingHistory: process.env.VUE_APP_SHARING_HISTORY_URL,
        galleries: process.env.VUE_APP_GALLERIES_URL,
        images: process.env.VUE_APP_IMAGES_URL,
        gallery: process.env.VUE_APP_DXA_ENDPOINT,
        exhibition: "https://exhibitions.museumwnf.org",
      },
      emails: {
        contactUs: process.env.VUE_APP_CONTACT_US_EMAIL,
      },
      uid: "",
      // Font Awesome won't load the SVGs inside the v-html text :(
      svgShare: `<svg xmlns="http://www.w3.org/2000/svg" class="font-awesome-svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M307 34.8c-11.5 5.1-19 16.6-19 29.2v64H176C78.8 128 0 206.8 0 304C0 417.3 81.5 467.9 100.2 478.1c2.5 1.4 5.3 1.9 8.1 1.9c10.9 0 19.7-8.9 19.7-19.7c0-7.5-4.3-14.4-9.8-19.5C108.8 431.9 96 414.4 96 384c0-53 43-96 96-96h96v64c0 12.6 7.4 24.1 19 29.2s25 3 34.4-5.4l160-144c6.7-6.1 10.6-14.7 10.6-23.8s-3.8-17.7-10.6-23.8l-160-144c-9.4-8.5-22.9-10.6-34.4-5.4z"/></svg>`,
      svgExternal: `<svg xmlns="http://www.w3.org/2000/svg" class="font-awesome-svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M352 0c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9L370.7 96 201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L416 141.3l41.4 41.4c9.2 9.2 22.9 11.9 34.9 6.9s19.8-16.6 19.8-29.6V32c0-17.7-14.3-32-32-32H352zM80 32C35.8 32 0 67.8 0 112V432c0 44.2 35.8 80 80 80H400c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32V432c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16H192c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z"/></svg>`,
    };
  },
  computed: {
    // Get available language names (from language "codes")
    availableLanguages: function () {
      let array = [];
      let languages = this.languages;
      for (let i = 0; i < languages.length; i++) {
        array.push(this.languageNames[languages[i].rel]);
      }
      // Create string for displaying at the end of EIAC message
      let string = array.join(", ");
      string = `<span style='font-weight: bold; font-style: italic'> ${string}</span>`;
      return string;
    },
    // Filter out empty/null data values
    filteredObjectData: function () {
      return this.filterData(this.objectData);
    },
    filteredCitationData: function () {
      return this.filterData(this.citationData);
    },
    // Show relatedObjects either in selected language
    // if available, or main language if not
    editedRelatedObjects: function () {
      let data;
      let alt = false;
      if (this.checkExists(this.relatedObjects)) {
        data = this.relatedObjects;
      } else if (this.checkExists(this.relatedObjectsAlt)) {
        data = this.relatedObjectsAlt;
        alt = true;
      }
      // Remove extraneous . from string
      for (let i = 0; i < data.length; i++) {
        let name = data[i].name;
        if (name.slice(-1) === ".") {
          data[i].name = name.substring(0, name.length - 1);
        }
        // If showing objects in main language but
        // selected language is not main language,
        // remove the justification text (hide)
        if (alt) {
          data[i].justification = null;
        }
      }
      return data;
    },
    // Sort Glossary terms alphabetically
    sortedGlossaryResults: function () {
      let array = Object.keys(this.glossaryResults);
      let alphabetized = array.sort((a, b) => {
        if (a[1] < b[1]) {
          return -1;
        } else if (a[1] > b[1]) {
          return 1;
        }
        return 0;
      });
      return alphabetized;
    },
    // Filter out dynasties without history data
    filteredDynastyData: function () {
      let filtered = [];
      if (!this.dynasties) {
        return null;
      }
      for (let i = 0; i < this.dynasties.length; i++) {
        if (this.dynasties[i].history.length !== 0) {
          filtered.push(this.dynasties[i]);
        }
      }
      return filtered;
    },
  },
  watch: {
    // Run getObjectData again on route change
    $route(to, from) {
      if ((to.params.language !== from.params.language) && 
        this.$route.path.includes("/database-item")) {
        this.getObjectData();
        loadLocaleMessages(this.currentLanguage);
      }
    },
    authorHoverIndex() {
      console.log(this.authorHoverIndex);
    },
  },
  methods: {
    getWorldImage: function () {
      return require(`../sites/${process.env.VUE_APP_ENV}/world.png`);
    },
     // Apply class according to source project
    backgroundColor: function (project, isExhibition) {
      return {
        DGA: project === "DGA",
        ISLandEPM: project === "ISL" || project === "EPM",
        AWE: project === "AWE",
        DBA: project === "DBA" || project === "BAR",
        Galleries: project === "GALLERIES",
        DCA: project === "DCA",
        EXH: isExhibition,
      };
    },
    // Close all other popups when a popup is selected
    showPopup: function (show, index) {
      let list = ["showGlossaryTool", "showDynasty", "showTimeline"];
      for (let i = 0; i < list.length; i++) {
        if (list[i] === show) {
          // Dynasty uses index
          if (show === "showDynasty") {
            this[list[i]] = index;
          } else {
          // Otherwise set to true
            this[list[i]] = true;
            // If Glossary, needs input focus
            if (show === "showGlossaryTool") {
              this.$nextTick(() => {
                this.$refs.glossaryInput.focus();
              });
            }
          }
        } else {
          // If Dynasty, set to null, otherwise false
          if (list[i] === "showDynasty") {
            this[list[i]] = null;
          } else {
            this[list[i]] = false;
          }
        }
      }
      // Close any glossary word popups
      for (let word in this.showGlossary) {
        this.showGlossary[word] = false;
      }
      // Close CV popups
      this.authorHover = null;
      this.authorType = null;
    },
    // Close related content popups when selecting a glossary word
    closePopups: function (gallery) {
      this.showGlossaryTool = false;
      this.showDynasty = null;
      this.showTimeline = false;
      if (gallery) {
        this.authorHover = null;
        this.authorType = null;
      }
    },
    // Filter out empty values (hide from display)
    // Used in computed methods
    filterData: function (obj) {
      let filtered = {};
      for (let key in obj) {
        if (obj[key][1]) {
          // If only short description is available
          // Re-label to just "Description"
          if (!this.bothDescsExist && key === "shortDescription") {
            filtered[key] = obj[key];
            filtered[key][0] = `${this.$i18n.t("objDescription")}`;
          } else {
            filtered[key] = obj[key];
          }
        }
      }
      return filtered;
    },
    // Changes route for language
    // getObjectData will run again, getting data in that language
    setLanguage: function (language) {
      if (language !== this.currentLanguage) {
        this.loaded = false;
        this.currentLanguage = language;
        // Hide/close pop-up windows
        this.showTimeline = false;
        this.showGlossaryTool = false;
        this.glossaryInput = "";
        this.selectedDefinition = "";
        this.showDynasty = false;
        for (let word in this.showGlossary) {
          this.showGlossary[word] = false;
        }
        this.authorHover = null;
        this.authorType = null;
        this.setRightToLeft(language);
        this.$router.push({ 
          name: "database-item", 
          params: { 
            pathMatch: this.uid.split('/'),
            language: this.currentLanguage 
          } 
        });
      }
    },
    // Prop used to set conditional dir
    setRightToLeft: function (language) {
      if (language === "ar") {
        this.rightToLeft = true;
      } else {
        this.rightToLeft = false;
      }
    },
    // Get the href for a matching rel (when no object name)
    getRelLink: (array, rel) => {
      for (let i = 0; i < array.length; i++) {
        if (array[i].rel === rel) {
          return array[i].href;
        }
      }
    },
    // Main function for this page
    getObjectData: function () {
      let path = this.$route.params.pathMatch.join("/");
      this.uid = path;
      let params = this.$route.params;
      let language = params.language ? params.language : "en";
      this.setRightToLeft(language);

      let itemURL = `${this.urls.gallery}/${path}/${language}`;

      let getObjectDataHelper = () => {
        return axios({ method: "GET", url: itemURL }).then(
          (result) => {
            // Short-hand variable
            let i18n = this.$i18n;
            this.languages = result.data.i18nLinks;

            this.photoData = result.data.pictures;
            if (this.photoData) {
              this.currentPhoto = this.photoData[0] ? this.photoData[0].imagePath : null;
              this.currentPhotoCaption = this.photoData[0] ? this.photoData[0].caption : null;
            }

            if (result.data.links) {
              for (let i = 0; i < result.data.links.length; i++) {
                if (result.data.links[i].rel === "remote-monument" ||
                  result.data.links[i].rel === "remote-object") {
                  this.sourceDatabase = result.data.links[i].href;
                }
              }
            }
            this.sourceDatabaseDomain = this.getSourceDomain(
              this.sourceDatabase
            );
            this.addToCollection = this.getRelLink(result.data.links, "remote-add-to-collection");

            this.project = result.data.projectId;
            this.isExhibition = result.data.projectSourceIsExhibition;
            this.setShowEIACmessage(this.project);
            this.copyedit = result.data.notice;

            // Set-up for in-description Glossary links
            // Short-hand variables for descriptions
            let description = result.data.description;
            let shortDescription = result.data.shortDescription;
            // List of glossary terms (one spelling)
            let words = [];
            // Array of objects, key = word, value = array of spellings
            let spellings = [];
            // Array of regexes created from spellings array
            let regexs = [];
            if (result.data.glossary) {
              this.glossary = result.data.glossary;
              // Populate the showGlossary object
              // Every word gets assigned value for displaying
              // Initially false (all hidden), set to true on click
              for (let i = 0; i < this.glossary.length; i++) {
                // Empty the showGlossary object (reset)
                for (let j = 0; j < this.glossary.length; j++) {
                  this.showGlossary = {};
                }
                // this.$set(this.showGlossary, this.glossary[i].name, false);
                this.showGlossary[this.glossary[i].name] = false;
              }
              // Push the names/keys for each word into words array
              for (let i = 0; i < this.glossary.length; i++) {
                words.push(this.glossary[i].name);
                let obj = {};
                // Add nested array for every word key
                obj[this.glossary[i].name] = [];
                // Push every spelling option into array
                // Remove extra whitespace characters
                for (
                  let j = 0;
                  j < this.glossary[i].spellings.length;
                  j++
                ) {
                  let trimmed = this.glossary[i].spellings[j].trim();
                  obj[this.glossary[i].name].push(trimmed);
                }
                // Format: [[ key: [key, ki, kee] ]]
                spellings.push(obj);
              }
              // Create array of regexs from spellings array
              for (let i = 0; i < words.length; i++) {
                let obj = {};
                // Key = word
                obj[words[i]] = [];
                // Get to array of spellings inside each word's object
                for (let j = 0; j < spellings[i][words[i]].length; j++) {
                  // Change word boundaries for Arabic ([^،-٩])
                  if (language === "ar") {
                    let regex = new RegExp(
                      `[^،-٩]${spellings[i][words[i]][j]}[^،-٩]`,
                      "gi"
                    );
                    obj[words[i]].push(regex);
                  } else {
                    let regex = new RegExp(
                      `\\b${spellings[i][words[i]][j]}(?:s?\\b)`,
                      "gi"
                    );
                    obj[words[i]].push(regex);
                  }
                }
                regexs.push(obj);
              }
              // Add anchor links to matching Glossary words in description(s)
              if (description) {
                description = this.searchAndWrap(
                  description,
                  words,
                  regexs
                );
              }
              if (shortDescription) {
                shortDescription = this.searchAndWrap(
                  shortDescription,
                  words,
                  regexs
                );
              }
            }

            // Get URL section for building Partner link
            // Unable to use vue-router here
            let urlIndex = window.location.href.indexOf("database");
            let urlSlice = window.location.href.slice(0, urlIndex);
            // Object data is saved here with its corresponding i18n label text
            this.objectData = {
              name: [`${i18n.t("objName")}`, result.data.name],
              aka: [`${i18n.t("objAKA")}`, result.data.alsoKnownAs],
              location: [
                `${i18n.t("objLocation")}`,
                `${result.data.location}, ${result.data.countryName}`,
              ],
              // Manually wrap the museum name with link to profile page
              // Unable to use vue-router here
              museum: [
                `${i18n.t("objHoldingMuseum")}`,
                `<a href="${urlSlice}partner/${result.data.projectId}/` +
                  `${result.data.countryId}/${result.data.partnerId}/en">` +
                  `${this.svgExternal}` +
                  `${result.data.holdingMuseum}</a>`,
              ],
              originalOwner: [
                `${i18n.t("objOriginalOwner")}`,
                result.data.originalOwner,
              ],
              currentOwner: [
                `${i18n.t("objCurrentOwner")}`,
                result.data.currentOwner,
              ],
              date: [`${i18n.t("objDate")}`, result.data.objectDate],
              artist: [`${i18n.t("objArtist")}`, 
                // result.data.artist],
                this.returnArtistData(result.data)],
              scribe: [`${i18n.t("objScribe")}`, result.data.scriber],
              workshop: [`${i18n.t("objWorkshop")}`, result.data.workshop],
              type: [`${i18n.t("objType")}`, result.data.typeOfObject],
              inventoryNumber: [
                `${i18n.t("objInventoryNumber")}`,
                result.data.inventoryId,
              ],
              materials: [
                `${i18n.t("objMaterials")}`,
                result.data.materials,
              ],
              dimensions: [
                `${i18n.t("objDimensions")}`,
                result.data.dimensions,
              ],
              dynasty: [
                `${i18n.t("objDynasty")}`,
                result.data.dynastyDescription,
              ],
              // artist: [`${i18n.t("objArtist")}`, result.data.artist],
              production: [
                `${i18n.t("objPlaceOfProduction")}`,
                result.data.placeOfProduction,
              ],
              provenance: [
                `${i18n.t("objProvenance")}`,
                result.data.provenance,
              ],
              binding: [`${i18n.t("objBinding")}`, result.data.binding],
              description: [`${i18n.t("objDescription")}`, description],
              // If short description shows, it will always be in a toggle-view section
              // Manually insert FA icon to indicate clickability
              shortDescription: [
                `${this.svgShare}${i18n.t(
                  "objShortDesc_show"
                )}`,
                shortDescription,
              ],
              // Declare catalogue to ensure correct order of fields
              // Catalogue content is determined below
              catalogue: [],
              obtention: [
                `${i18n.t("objObtentionMethod")}`,
                result.data.methodOfObtention,
              ],
              datation: [
                `${i18n.t("objDatationMethod")}`,
                result.data.methodOfDatation,
              ],
              provenanceMethod: [
                `${i18n.t("objProvenanceMethod")}`,
                result.data.methodOfProvenance,
              ],
              bibliography: [
                `${i18n.t("objBibliography")}`,
                result.data.bibliography,
              ],
            };
            // Check catalogue link for null before adding to objectData (filter out ahead of computed)
            if (result.data.catalogueHoldingLink) {
              this.objectData.catalogue = [
                `${i18n.t("objDigitLink")}`,
                `<a href="${result.data.catalogueHoldingLink}" target="_blank">` +
                  `<font-awesome-icon :icon="['fas', 'up-right-from-square']" />` +
                  `${result.data.catalogueHoldingLink}</a>`,
              ];
            }
            this.copyright = result.data.notice_b;
            // If both descriptions exist, both are shown but the short description is hidden
            // If only one exists, then whichever it is will be shown as "Description"
            if (
              this.objectData.description[1] &&
              this.objectData.shortDescription[1]
            ) {
              this.bothDescsExist = true;
              this.showShortDesc = false;
            } else {
              this.bothDescsExist = false;
              this.showShortDesc = true;
            }
            this.projectName = result.data.projectName;
            // Citation data is saved here, separate from objectData because the format is different
            this.citationData = {
              prepared: [
                `${i18n.t("objPreparedBy")}`,
                this.filterCitationAuthors(result.data.authors.writer),
              ],
              copyedited: [
                `${i18n.t("objCopyeditedBy")}`,
                this.filterCitationAuthors(result.data.authors.copyEditor),
              ],
              translation: [
                `${i18n.t("objTranslationBy")}`,
                this.filterCitationAuthors(result.data.authors.translator),
              ],
              translationCopyedited: [
                `${i18n.t("objTransCopyeditedBy")}`,
                this.filterCitationAuthors(
                  result.data.authors.translationCopyEditor
                ),
              ],
            };
            console.log(this.citationData);
            this.workingNumber = [
              `${i18n.t("objWorkingNumber")}`,
              result.data.workingNumber,
            ];

            this.relatedObjects = result.data.relatedObjects;
            this.relatedObjectsAlt = result.data.relatedObjectsMainLanguage;

            this.timelineInfo.country = result.data.countryId;
            this.timelineInfo.start = result.data.startDate;
            this.timelineInfo.end = result.data.endDate;
            this.setTimelineRounded(result.data.startDate);
            this.getTimelineCountries(
              this.timelineInfo.start,
              this.timelineInfo.end
            );
            this.getTimelineEvents(
              this.timelineInfo.country,
              this.timelineInfo.start,
              this.timelineInfo.end
            );

            this.dynasties = result.data.dynasties;

            this.audio = result.data.audios;
            this.video = result.data.videos;
            console.log(this.video);

            // Reset exhibitionData to empty array
            // exhibitionData changes depending on the source project
            // (Sharing History has a different format)
            this.exhibitionData = [];
            if (result.data.exhibitions) {
              for (let i = 0; i < result.data.exhibitions.length; i++) {
                if (!result.data.exhibitions[i].href ||
                  result.data.exhibitions[i].href.includes("exhibitions.museumwnf.org")) {
                  continue;
                } else {
                  let obj = {};
                  obj.project = result.data.exhibitions[i].project;
                  obj.exhibition = result.data.exhibitions[i].exhibition;
                  obj.theme = result.data.exhibitions[i].theme;
                  obj.href = result.data.exhibitions[i].href;
                  this.exhibitionData.push(obj);
                }
              }
            } else if (result.data.sharingHistoryExhibitions) {
              for (
                let i = 0;
                i < result.data.sharingHistoryExhibitions.length;
                i++
              ) {
                let obj = {};
                obj.project =
                  result.data.sharingHistoryExhibitions[i].project;
                obj.exhibition =
                  result.data.sharingHistoryExhibitions[i].exhibition;
                obj.theme = result.data.sharingHistoryExhibitions[i].theme;
                obj.subTheme =
                  result.data.sharingHistoryExhibitions[i]["sub-theme"];
                obj.href = result.data.sharingHistoryExhibitions[i].href;
                this.exhibitionData.push(obj);
              }
            }
            if (this.exhibitionData.length !== 0) {
              this.showExhibitions = true;
            } else {
              this.showExhibitions = false;
            }
            this.galleryData = result.data.galleries;
            // this.allExhibitions = result.data.x_all_exhibitions.filter(
            //   (entry) => entry.ThemeNumber !== 0 && 
            //   entry.SubThemeNumber !== "overview" &&
            //   entry.LanguageId === this.currentLanguage
            // );
            let allExhibitions = result.data.x_all_exhibitions.filter(
              (entry) => entry.ThemeNumber !== 0 && 
              entry.SubThemeNumber !== "overview" &&
              entry.LanguageId === this.currentLanguage
            );
            // If no entries (no language options) filter again for default English entries
            if (allExhibitions.length === 0) {
              allExhibitions = result.data.x_all_exhibitions.filter(
                (entry) => entry.ThemeNumber !== 0 && 
                entry.SubThemeNumber !== "overview" && 
                entry.LanguageId === "en"
              );
            }
            this.allExhibitions = this.sortAlphabetical(allExhibitions);

            this.travelBookLink = this.getRelLink(result.data.links, "remote-book");

            this.loaded = true;

            // If Glossary data exists, then add click events
            // to show definition window when clicking a word
            // nextTick is required due to v-html, adding event listeners
            // after Vue has updated (using refs)
            if (result.data.glossary) {
              this.$nextTick(() => {
                if (description) {
                  this.addClicksToGlossary(
                    this.$refs.description,
                    words,
                    spellings,
                    regexs
                  );
                }
                if (shortDescription) {
                  this.addClicksToGlossary(
                    this.$refs.shortDescription,
                    words,
                    spellings,
                    regexs
                  );
                }
              });
            }
          },
          (error) => {
            // Redirect if there is an error with the axios request
            this.$router.replace({ name: "error" });
            console.error(error);
          }
        );
      }
      getObjectDataHelper();
    },
    // Function for photo slideshow
    slideshow: function (clicked) {
      let urls = [];
      for (let i = 0; i < this.photoData.length; i++) {
        urls.push(this.photoData[i].imagePath);
      }
      let index = urls.indexOf(this.currentPhoto);
      let newIndex;
      if (clicked === "right") {
        if (index === urls.length - 1) {
          newIndex = 0;
        } else {
          newIndex = index + 1;
        }
      } else if (clicked === "left") {
        if (index === 0) {
          newIndex = urls.length - 1;
        } else {
          newIndex = index - 1;
        }
      }
      this.currentPhoto = urls[newIndex];
    },
    // Get portion of URL: "x.museumfwnf.org"
    // Indicates project of object
    getSourceDomain: function (source) {
      let sections = source.split("/");
      return sections[2];
    },
    // Check argument - return false if empty or null
    checkExists: function (property) {
      if (!property) {
        return false;
      } else if (property.length === 0) {
        return false;
      } else if (Object.entries(property).length === 0) {
        return false;
      } else {
        return true;
      }
    },
    // An EIAC/EPM object will always have this message showing
    setShowEIACmessage: function (project) {
      if (project === "EPM") {
        this.showEIACmessage = true;
      } else {
        this.showEIACmessage = false;
      }
    },
    // Get list of languages available in project
    // Used for EIAC message
    getLanguageNames: function () {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/languages?hash=${process.env.VUE_APP_API_HASH}`,
      }).then(
        (result) => {
          this.languageNames = result.data;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    toggleShortDesc: function (key) {
      // Exit function if only one description exists
      if (!this.bothDescsExist) {
        return;
      }
      // Exit function if label clicked isn't Short Description
      if (key !== "shortDescription") {
        return;
      }
      this.showShortDesc = !this.showShortDesc;
      // Toggle the label text (hide/show)
      if (this.showShortDesc) {
        this.objectData.shortDescription[0] = `<font-awesome-icon :icon="['fas', 'share']" />${this.$i18n.t(
          "objShortDesc_hide"
        )}`;
      } else if (!this.showShortDesc) {
        this.objectData.shortDescription[0] = `<font-awesome-icon :icon="['fas', 'share']" />${this.$i18n.t(
          "objShortDesc_show"
        )}`;
      }
    },
    // Search provided text for regex, wrap matches in links
    searchAndWrap: function (text, words, regexs) {
      for (let i = 0; i < regexs.length; i++) {
        // Get to array of regexs inside each word's object
        for (let j = 0; j < regexs[i][words[i]].length; j++) {
          text = text.replace(regexs[i][words[i]][j], (match) => {
            return `<a class="glossary-link">${match}</a>`;
          });
        }
      }
      return text;
    },
    // Using refs, add click events to v-html text
    addClicksToGlossary: function (ref, words, spellings, regexs) {
      // Number of children (tags) inside description
      for (let i = 0; i < ref[0].children.length; i++) {
        for (let j = 0; j < regexs.length; j++) {
          // Get to array of regexs for each word
          for (let k = 0; k < regexs[j][words[j]].length; k++) {
            // Boolean for whether innerText of tags in description matches regex
            // Loop adds the correct click event to each instance of the matching word
            if (regexs[j][words[j]][k].test(ref[0].children[i].innerText)) {
              regexs[j][words[j]][k].lastIndex = 0;
              // If the element is not a.glossary-link
              // (Match triggered by i tag that contains a.glossary-link)
              if (ref[0].children[i].className !== "glossary-link") {
                // Check each child inside the i tag
                for (let l = 0; l < ref[0].children[i].children.length; l++) {
                  // Repeat of earlier test, but for inner children
                  if (
                    regexs[j][words[j]][k].test(
                      ref[0].children[i].children[l].innerText
                    )
                  ) {
                    regexs[j][words[j]][k].lastIndex = 0;
                    // Same code as below (refactor needed) but adds listener to the child
                    ref[0].children[i].children[l].addEventListener(
                      "click",
                      (e) => {
                        e.preventDefault();
                        let clickX = e.pageX;
                        let clickY = e.pageY;
                        // Larger widths: opens glossary window centered/above clicked area
                        // Smaller widths: opens glossary window in the middle of the page
                        // (top aligned with clicked area)
                        if (!this.lessThan974) {
                          this.$refs[words[j]][0].style.top = `${clickY}px`;
                          this.$refs[words[j]][0].style.left = `${clickX}px`;
                          this.$refs[
                            words[j]
                          ][0].style.transform = `translate(-50%, -110%)`;
                        } else if (this.lessThan974) {
                          this.$refs[words[j]][0].style.top = `${clickY}px`;
                          this.$refs[words[j]][0].style.left = "50%";
                          this.$refs[words[j]][0].style.transform =
                            "translateX(-50%)";
                        }
                        for (let m = 0; m < words.length; m++) {
                          this.showGlossary[words[m]] = false;
                        }
                        // Get the spelling of the word that matches the regex for displaying
                        this.currentWord = spellings[j][words[j]][k];
                        this.showGlossary[words[j]] = true;
                      }
                    );
                  }
                }
              } else {
                // If regex matches, add an event listener to open a glossary window
                ref[0].children[i].addEventListener("click", (e) => {
                  e.preventDefault();
                  let clickX = e.pageX;
                  let clickY = e.pageY;
                  if (!this.lessThan974) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = `${clickX}px`;
                    this.$refs[
                      words[j]
                    ][0].style.transform = `translate(-50%, -110%)`;
                  } else if (this.lessThan974) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = "50%";
                    this.$refs[words[j]][0].style.transform =
                      "translateX(-50%)";
                  }
                  for (let m = 0; m < words.length; m++) {
                    this.showGlossary[words[m]] = false;
                  }
                  // Get the spelling of the word that matches the regex for displaying
                  this.currentWord = spellings[j][words[j]][k];
                  this.showGlossary[words[j]] = true;
                });
              }
            }
          }
        }
      }
    },
    hideGlossary: function (word) {
      this.showGlossary[word] = false;
    },
    // Create string for Artist fields
    returnArtistData: function (data) {
      let array = [];
      if (data.artist) {
        array.push(data.artist);
      }
      if (data.artistDateOfBirth || data.artistPlaceOfBirth || data.artistDateOfDeath || data.artistPlaceOfDeath) {
        array.push("<br />");
      }
      if (data.artistDateOfBirth) {
        array.push(data.artistDateOfBirth);
        if (data.artistPlaceOfBirth) {
          array.push(", ");
        }
      }
      if (data.artistPlaceOfBirth) {
        array.push(data.artistPlaceOfBirth);
      }
      if ((data.artistDateOfBirth || data.artistPlaceOfBirth) && (data.artistDateOfDeath || data.artistPlaceOfDeath)) {
        array.push(" - ");
      }
      if (data.artistDateOfDeath) {
        array.push(data.artistDateOfDeath);
        if (data.artistPlaceOfDeath) {
          array.push(", ");
        }
      }
      if (data.artistPlaceOfDeath) {
        array.push(data.artistPlaceOfDeath);
      }
      if (data.artistPeriodOfActivity) {
        array.push("<br />");
        array.push(data.artistPeriodOfActivity);
      }
      console.log(array.join(""));
      return array.join("");
    },
    // May be more than one name for citation fields
    // Separate key for Arabic names, show when selected language is Arabic
    filterCitationAuthors: function (data) {
      if (!data) {
        return null;
      }
      let ar = this.$i18n.locale === "ar";
      let array = [];
      let names = [];
      for (let i = 0; i < data.length; i++) {
        let info = {};
        if (ar) {
          if (data[i].ar) {
            // info.push(data[i].ar);
            info.name = data[i].ar;
            names.push(data[i].ar);
          } else {
            // info.push(data[i].en);
            info.name = data[i].en;
            names.push(data[i].en);
          }
        } else {
          // info.push(data[i].en);
          info.name = data[i].en;
          names.push(data[i].en);
        }
        if (data[i].curriculum) {
          // info.push(data[i].curriculum);
          info.curriculum = data[i].curriculum;
        }
        array.push(info);
      }
      let allNames = names.join(", ");
      let authors = {};
      authors.info = array;
      authors.string = allNames;
      return authors;
      // return array.join(", ");
    },
    // this.years is available from this.loadYears
    // Contains an array of year increments + text labels
    // Format: [[ 500, "500 B.C." ]]
    setTimelineRounded: function (objStart) {
      let years = [];
      let start, end;
      for (let i = 0; i < this.years.length; i++) {
        years.push(this.years[i][0]);
      }
      // Find closest year increments to object's dates
      for (let j = 0; j < years.length; j++) {
        if (objStart > years[j]) {
          start = years[j];
        } else {
          end = years[j];
          break;
        }
      }
      // Set to closest year increments to object's dates
      this.timelineInfo.startRounded = start;
      this.timelineInfo.endRounded = end;
    },
    // Get list of countries that have event data for provided time range
    getTimelineCountries: function (start, end) {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/events/countries?hash=${process.env.VUE_APP_API_HASH}&na=${start}&nz=${end}`,
      }).then(
        (result) => {
          // Returns format (example): { 0: { id: "dz", name: "Algeria" }}
          let objects = result.data.items;
          let array = [];
          // Pushes (example) ["dz", "Algeria"] into array
          for (let entry in objects) {
            let subArray = [];
            for (let key in objects[entry]) {
              subArray.push(objects[entry][key]);
            }
            array.push(subArray);
          }
          // Alphabetize the array for display purposes
          let alphabetized = array.sort((a, b) => {
            if (a[1] < b[1]) {
              return -1;
            } else if (a[1] > b[1]) {
              return 1;
            }
            return 0;
          });
          this.timelineCountries = alphabetized;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    getTimelineEvents: function (country, start, end) {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/events?hash=${process.env.VUE_APP_API_HASH}&ic[]=${country}&na=${start}&nz=${end}`,
      }).then(
        (result) => {
          this.timeline = result.data.items;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    showTimelinePopout: function () {
      this.showGlossaryTool = false;
      this.showDynasty = null;
      this.showTimeline = true;
    },
    selectText: function () {
      let input = this.$refs.glossaryInput;
      input.focus();
      input.select();
    },
    // Wait 300ms after keyup before initiating the search
    initiateGlossarySearch: function (language, input) {
      clearTimeout(this.glossaryTimeout);
      // Don't run if the field is empty
      if (input !== "") {
        this.glossaryTimeout = setTimeout(() => {
          this.searchGlossary(language, input);
        }, 300);
      }
    },
    searchGlossary: function (language, input) {
      this.glossaryResults = {};
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/mwnf3/glossary?hash=${process.env.VUE_APP_API_HASH}&languageId=${language}&spelling=${input}`,
      }).then(
        (result) => {
          let glossaryResults = result.data;
          let object = {};
          // Get definition of each word that matches input
          // "Save" using word's key, not spelling
          // (Key may not match user's input)
          // Format: { word: definition }
          for (let word in glossaryResults) {
            // Only return word if the first letter matches user input's first letter
            if (
              input.charAt(0).toLowerCase() === word.charAt(0).toLowerCase()
            ) {
              object[word] = glossaryResults[word][language].definition;
            }
          }
          this.glossaryResults = object;
          this.showWordList = true;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    selectWord: function (word) {
      this.glossaryInput = word;
      this.showWordList = false;
      this.selectedDefinition = this.glossaryResults[word];
    },
    showGlossaryPopout: function () {
      this.showTimeline = false;
      this.showDynasty = null;
      this.showGlossaryTool = true;
      this.$nextTick(() => {
        this.$refs.glossaryInput.focus();
      });
    },
    showDynastyPopout: function (index) {
      this.showTimeline = false;
      this.showGlossaryTool = false;
      this.showDynasty = index;
    },
    printPage: function () {
      window.print();
    },
    positionHover: function (e) {
      let author = e.target.offsetTop;
      let cvs = e.target.getElementsByClassName("info-authors-cv");
      cvs[0].style.display = "block";
      let height = cvs[0].offsetHeight;
      cvs[0].style.display = "none";
      cvs[0].style.top = author - height - 10 + "px";
    },
    sortAlphabetical: function (exhibitions) {
      let sorted = exhibitions.sort(function (a, b) {
        let aWords = a["ExhibitionTitle"].toLowerCase().split(" ");
        let bWords = b["ExhibitionTitle"].toLowerCase().split(" ");
        let aExcluded = aWords.filter(word => !["a", "an", "the"].includes(word));
        let bExcluded = bWords.filter(word => !["a", "an", "the"].includes(word));
        return aExcluded.join(" ").localeCompare(bExcluded.join(" "), "en", { sensitivity: "base" });
      });
      return sorted;
    }
  },
  created() {
    // Insert citation link when copy/pasting from site
    window.addEventListener("copy", (e) => {
      let selection = document.getSelection();
      let citation = `\n\nSource: [${this.sourceDatabase}]`;
      e.clipboardData.setData("text/plain", selection + citation);
      e.preventDefault();
    });
  },
  mounted() {
    loadLocaleMessages(this.$route.params.language);
    this.currentLanguage = this.$route.params.language;
    this.getObjectData();
    this.getLanguageNames();
    this.loadYears("");
  },
  beforeRouteEnter(to, from, next) {
    loadLocaleMessages(to.params.language).then(() => next());
  },
  beforeRouteUpdate(to, from) {
    if (to.params.language !== from.params.language) {
      // Do not use loadLocaleMessages, that sets global locale
      // this.$i18n.locale is for this component (switching item language)
      this.$i18n.locale = to.params.language;
    }
  }
};
</script>


<style scoped lang="scss">

.italic {
  font-style: italic;
}
.bold {
  font-weight: bold;
}
// Sizing the SVG icons
#database-page-wrapper :deep(.font-awesome-svg) {
  height: 16px;
  width: 16px;
  vertical-align: -0.125em;
  margin-right: 5px;
  // @include font-11;
}
// Size and add padding to all FA icons
// v-deep is for icons nested within v-html
#database-page-wrapper :deep(.fas) {
  font-size: 75%;
  padding-right: 4px;
}
// Reverse padding side when RTL
#database-page-wrapper :deep(.fas-rtl .fas) {
  padding-right: 0;
  padding-left: 4px;
}
// Close button has no padding even with RTL
#database-page-wrapper #timeline-popout-close .fas,
#database-page-wrapper #dynasty-popout-close .fas,
#database-page-wrapper #glossary-tool-popout-close .fas,
#database-page-wrapper .info-authors-cv-close .fas {
  padding: 0;
}
#database-page-wrapper #timeline-popout-link .fas {
  padding-left: 0;
  padding-right: 4px;
}

#database-page-wrapper {
  width: 100%;
}

.loader-container {
  min-height: 500px;
}

#languages {
  @include flex-center;
  justify-content: flex-end;
  background-color: $theme-dark;
  padding-right: 10px;
}
.languages-button {
  @include button-styles;
  color: $light-text;
  font-size: 95%;
  padding: 4px 8px;
}
.language-selected {
  background-color: $theme-light;
}

.glossary-entry-container {
  width: 400px;
  position: absolute;
  z-index: 68;
  background-color: $theme-light;
  border-radius: 10px;
  color: $light-text;
}
.glossary-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: -10px;
  right: -15px;
  background-color: $theme-medium-dark;
  border-radius: 100%;
}
// Override size and padding for FA icon
#database-page-wrapper .glossary-close .fas {
  font-size: 100%;
  padding-right: 0;
}
.glossary-header {
  @include flex-center;
  justify-content: flex-start;
  height: 60px;
  font-size: 110%;
  font-weight: bold;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
  background-color: $theme-light;
  padding: 0 15px;
  border-radius: 10px 10px 0 0;
}
.glossary-entry {
  max-height: 150px;
  overflow: auto;
  position: relative;
  background-color: white;
  color: black;
  margin: 0 15px 15px 15px;
}
.glossary-word {
  @include flex-center;
  justify-content: flex-start;
  height: 40px;
  color: black;
  background-color: #f0ede4;
  padding: 0 15px;
}
.glossary-definition {
  padding: 15px;
}

#database-object-container {
  min-height: 650px;
  width: 100%;
  background-color: white;
  padding: 0 50px;
  position: relative;
}

#back {
  @include flex-center;
  justify-content: flex-start;
  // Height from Related Content section
  // Padding of 20px, margin-top of header of 30px
  height: 50px;
}
#back button {
  @include button-styles;
  padding: 2px;
  font-weight: bold;
}

#database-object-wrapper {
  @include flex-center;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
}

#photo-info-wrapper {
  @include flex-center;
  justify-content: flex-start;
  align-items: flex-start;
}

#photo-container {
  width: 30%;
  padding: 0 10px 50px 0;
}
.photo {
  width: 100%;
  cursor: zoom-in;
}
#thumbnail-container {
  @include flex-center;
  justify-content: flex-start;
  flex-wrap: wrap;
}
.thumbnail {
  height: 60px;
  width: 60px;
  position: relative;
  margin: 10px 10px 0 0;
  cursor: pointer;
}
.thumbnail:hover .tooltip-text {
  visibility: visible;
}
.thumbnail img {
  @include image-scale;
  object-fit: cover;
}
.tooltip-text {
  @include flex-center;
  flex-direction: column;
  height: 60px;
  position: absolute;
  bottom: 0;
  left: 110%;
  background-color: rgba($theme-dark, 0.95);
  color: $light-text;
  font-size: 90%;
  text-align: center;
  white-space: nowrap;
  padding: 5px 10px;
  border-radius: 6px;
  visibility: hidden;
  pointer-events: none;
  z-index: 65;
}
.tooltip-text::after {
  content: "";
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 100%;
  border-width: 5px;
  border-style: solid;
  border-color: transparent rgba($theme-dark, 0.95) transparent transparent;
}
#lightbox-container {
  @include flex-center;
  height: 100vh;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  background-color: rgba(black, 0.9);
}
#lightbox {
  height: 90%;
  width: 90%;
}
#lightbox img {
  @include image-scale;
  object-fit: contain;
}
.lightbox-control {
  position: absolute;
  font-size: 30px;
}
.lightbox-control button {
  @include flex-center;
  @include button-styles;
  height: 45px;
  width: 45px;
  border: 2px solid white;
  border-radius: 100%;
  color: white;
}
#lightbox-left {
  left: 10px;
}
#lightbox-right {
  right: 10px;
}
// Reset padding for FA icons
#lightbox-left .fas,
#lightbox-right .fas {
  padding: 0;
}
.current-caption {
  margin-top: 20px;
}
#links-container {
  margin-top: 20px;
}
#links-container p,
#links-container a {
  padding: 2px 5px 2px 0;
}
#links-container a {
  color: black;
  text-decoration: none;
}
#links-container a:hover {
  @include link-hover-styles;
}

#info-container {
  width: 45%;
  padding: 0 10px 50px 10px;
}
#info-eiac {
  font-style: italic;
}
#info-eiac :deep(span) {
  font-style: normal;
}
#info-eiac,
#info-copyedit {
  font-size: 90%;
  margin-bottom: 25px;
}
.info-label {
  font-weight: bold;
}
.info-label-short {
  margin-bottom: 10px;
}
// For "Short Description" label
.pointer {
  cursor: pointer;
}
// v-deep for targeting nested <a> inside v-html
.info-link :deep(a) {
  @include link-styles;
  color: black;
}
.info-link :deep(a:hover) {
  @include link-hover-styles;
}
.info,
#info-citation {
  padding-left: 10px;
  margin-bottom: 10px;
}
#info-project-name,
#info-citation-source {
  font-style: italic;
}
#info-citation-source {
  display: block;
  overflow-wrap: break-word;
  word-break: break-all;
}
.info-authors {
  display: flex;
  // position: relative;
  // padding-left: 10px;
}
.info-authors-label {
  padding-left: 10px;
  margin-right: 3px;
}
.info-authors-name {
  // position: relative;
  cursor: default;
}
.cv-hover {
  @include link-styles;
  color: $link-blue;
}
.cv-hover:hover {
  @include link-hover-styles;
}
.info-authors-comma {
  margin-right: 2px;
}
.info-authors-cv {
  position: absolute;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  height: 300px;
  width: 500px;
  color: black;
  background-color: $theme-light;
  border: 3px solid $theme-medium;
  border-radius: 10px;
  padding: 20px;
  overflow: auto;
}
.info-authors-cv-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  left: 50%;
  -webkit-transform: translateX(calc(-50% + 250px));
  transform: translateX(calc(-50% + 250px)) translateY(calc(50% - 360px));
  background-color: $theme-dark;
  border: 3px solid $theme-medium;
  border-radius: 100%;
  z-index: 5;
}
/* Override size and padding for other FA icons */
.info-authors-cv-close .fas {
  font-size: 100%;
  padding-right: 0;
}
.info-working-number {
  padding-top: 10px;
  padding-left: 10px;
}
:deep(.glossary-link) {
  @include link-styles;
  cursor: pointer;
}
:deep(.glossary-link:hover) {
  @include link-hover-styles;
}
#info-citation-link {
  margin-top: 10px;
  padding: 10px;
  overflow-wrap: break-word;
  word-break: break-all;
}
#info-citation-link a:hover {
  @include link-hover-styles;
}
// Colors for source link depending on project
.DGA {
  background-color: $dga-blue;
  color: $light-text;
  a {
    color: $light-text;
    text-decoration: none;
  }
}
.ISLandEPM {
  background-color: $dia-yellow;
  color: black;
  a {
    color: black;
    text-decoration: none;
  }
}
.AWE {
  background-color: $sh-red;
  color: $light-text;
  a {
    color: $light-text;
    text-decoration: none;
  }
}
.DBA {
  background-color: $dba-blue;
  color: $light-text;
  a {
    color: $light-text;
    text-decoration: none;
  }
}
.Galleries {
  background-color: $g-grey;
  color: $light-text;
  a {
    color: $light-text;
    text-decoration: none;
  }
}
.DCA {
  background-color: $dca-tan;
  color: $light-text;
  a {
    color: $light-text;
    text-decoration: none;
  }
}
.EXH {
  background-color: $exhibitions-color;
  color: white;
  a {
    color: white;
    text-decoration: none;
  }
}

#related-content-container {
  width: 25%;
  position: absolute;
  top: 0;
  right: 0;
  padding: 20px 20px 40px 20px;
  background-color: $theme-light;
  color: $light-text;
}
#related-content-container a {
  color: $light-text;
  text-decoration: none;
}
#related-content-container a:hover {
  @include link-hover-styles;
}
.related-header {
  @include text-color;
  font-size: 110%;
  font-weight: bold;
  text-decoration: underline;
  margin: 30px 0 10px 0;
}
.related-margin {
  margin-bottom: 10px;
}
// Additional spacing if preceding section is visible
.conditional-margin {
  margin-top: 10px;
}
#related-description {
  @include text-color;
  font-style: italic;
  font-size: 90%;
}
#related-objects-container {
  @include flex-center;
  flex-direction: column;
  align-items: flex-start;
}
#related-objects-wrapper {
  @include flex-center;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
  margin: -10px -10px 0 0;
}
.related-objects {
  height: 64px;
  width: 64px;
  position: relative;
  margin: 5px 10px 5px 0;
  cursor: pointer;
}
.related-objects:hover .related-tooltip {
  visibility: visible;
}
.related-objects img {
  @include image-scale;
  object-fit: cover;
}
.related-tooltip {
  @include flex-center;
  flex-direction: column;
  height: 68px;
  position: absolute;
  bottom: 0;
  right: 110%;
  background-color: rgba($theme-dark, 0.95);
  color: $light-text;
  font-size: 90%;
  text-align: center;
  white-space: nowrap;
  padding: 5px 10px;
  border-radius: 6px;
  visibility: hidden;
  pointer-events: none;
  z-index: 70;
}
.related-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 100%;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent rgba($theme-dark, 0.95);
}

#timeline-container {
  position: relative;
}
#timeline {
  cursor: pointer;
}
#timeline:hover {
  @include link-hover-styles;
}
#timeline-popout {
  min-height: 150px;
  max-height: 450px;
  width: 500px;
  position: absolute;
  top: 0;
  right: calc(100% + 20px + 20px);
  background-color: $theme-light;
  border-radius: 10px;
}
#timeline-popout-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: -10px;
  right: -15px;
  background-color: $theme-medium-dark;
  border-radius: 100%;
}
/* Override size and padding for other FA icons */
#timeline-popout-close .fas {
  font-size: 100%;
  padding-right: 0;
}
#timeline-popout-title {
  @include flex-center;
  justify-content: flex-start;
  height: 60px;
  font-size: 110%;
  font-weight: bold;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
  background-color: $theme-light;
  padding-left: 15px;
  border-radius: 10px 10px 0 0;
}
#timeline-popout-option {
  @include flex-center;
  flex-direction: column;
  align-items: flex-start;
  height: 65px;
  width: 100%;
  background-color: $theme-light;
  padding-left: 15px;
}
#timeline-popout-option div {
  width: 100%;
  padding-bottom: 10px;
}
#timeline-popout-option label {
  margin-right: 10px;
}
#timeline-popout-scroll {
  max-height: 275px;
  overflow: auto;
  margin: 0 15px 15px 15px;
}
#timeline-scroll-subheader {
  @include flex-center;
  justify-content: flex-start;
  height: 40px;
  color: black;
  background-color: #f0ede4;
  padding-left: 15px;
}
#timeline-scroll-text {
  position: relative;
  color: black;
  background-color: white;
}
#timeline-scroll-text > div {
  padding: 10px 15px;
}
#timeline-scroll-text > div:nth-child(even) {
  background-color: #f0ede4;
}
.timeline-date {
  font-weight: bold;
  padding-bottom: 5px;
}

#glossary-tool-container {
  position: relative;
}
#glossary-tool {
  cursor: pointer;
}
#glossary-tool:hover {
  @include link-hover-styles;
}
#glossary-tool-popout {
  min-height: 150px;
  max-height: 450px;
  width: 500px;
  position: absolute;
  top: 0;
  right: calc(100% + 20px + 20px);
  background-color: $theme-light;
  border-radius: 10px;
}
#glossary-tool-popout-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: -10px;
  right: -15px;
  background-color: $theme-medium-dark;
  border-radius: 100%;
}
/* Override size and padding for other FA icons */
#glossary-tool-popout-close .fas {
  font-size: 100%;
  padding-right: 0;
}
#glossary-tool-popout-title {
  @include flex-center;
  justify-content: flex-start;
  height: 60px;
  font-size: 110%;
  font-weight: bold;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
  background-color: $theme-light;
  padding: 0 15px;
  border-radius: 10px 10px 0 0;
}
#glossary-tool-instructions {
  padding: 0 15px;
}
#glossary-tool-instructions p {
  padding: 10px 15px;
  color: black;
  background-color: #f0ede4;
}
#glossary-tool-input {
  position: relative;
  width: 100%;
  padding: 10px 15px;
}
#glossary-tool-input input {
  width: 100%;
  padding: 5px;
}
#glossary-tool-list {
  position: absolute;
  max-height: 150px;
  width: calc(100% - 30px);
  overflow: auto;
  color: black;
  background-color: white;
  border: 1px solid $theme-medium-dark;
}
#glossary-tool-list ul {
  list-style-type: none;
}
#glossary-tool-list li {
  cursor: pointer;
  padding: 2px 5px;
}
.glossary-hover {
  background-color: #f0ede4;
}
#glossary-tool-scroll {
  max-height: 275px;
  overflow: auto;
  margin: 0 15px 15px 15px;
}
#glossary-tool-text {
  color: black;
  background-color: white;
}
#glossary-tool-text p {
  padding: 10px 15px;
}
#glossary-tool-definition-label {
  font-weight: bold;
  background-color: #f0ede4;
}

.dynasty-wrapper {
  position: relative;
}
.dynasty {
  cursor: pointer;
}
.dynasty:hover {
  @include link-hover-styles;
}
#dynasty-popout {
  min-height: 150px;
  max-height: 450px;
  width: 500px;
  position: absolute;
  top: 0;
  right: calc(100% + 20px + 20px);
  background-color: $theme-light;
  border-radius: 10px;
}
#dynasty-popout-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: -10px;
  right: -15px;
  background-color: $theme-medium-dark;
  border-radius: 100%;
}
/* Override size and padding for other FA icons */
#dynasty-popout-close .fas {
  font-size: 100%;
  padding-right: 0;
}
#dynasty-popout-title {
  @include flex-center;
  justify-content: flex-start;
  height: 60px;
  font-size: 110%;
  font-weight: bold;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
  background-color: $theme-light;
  padding-left: 15px;
  border-radius: 10px 10px 0 0;
}
#dynasty-popout-scroll {
  max-height: 275px;
  overflow: auto;
  margin: 0 15px 15px 15px;
}
#dynasty-name {
  @include flex-center;
  justify-content: flex-start;
  height: 40px;
  color: black;
  background-color: #f0ede4;
  padding-left: 15px;
}
#dynasty-text {
  position: relative;
  color: black;
  background-color: white;
  /* Ensure text is above "padding" (below) */
  padding: 10px 15px 15px 15px;
}
#dynasty-text p {
  padding-bottom: 10px;
}
.dynasty-area {
  font-weight: bold;
}

.audio-link,
.video-link {
  display: inline-block;
  position: relative;
}
.audio-link:hover .related-tooltip,
.video-link:hover .related-tooltip {
  visibility: visible;
}

#exhibition-project,
.exhibit span {
  font-style: italic;
}
.exhibit span span {
  font-style: normal;
}

#exhibitions,
#new-exhibitions {
  margin-bottom: 10px;
}
  .new-exhibition-title {
    margin-top: 10px;
  }
  .first-exhibition-title {
    margin-top: 0;
  }

.download {
  cursor: pointer;
}
.download:hover {
  @include link-hover-styles;
}

/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #lightbox {
    position: relative;
  }
  #lightbox img {
    max-height: 100%;
    max-width: 100%;
    height: auto;
    width: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }
}

@media only screen and (max-width: 974px) {
  #database-object-container {
    min-height: initial;
  }
  #database-object-wrapper {
    flex-direction: column;
  }
  #photo-info-wrapper {
    width: 100%;
  }
  #photo-container {
    width: 40%;
  }
  .lightbox-control button {
    height: 40px;
    width: 40px;
  }
  #info-container {
    width: 60%;
    padding-right: 0;
    padding-bottom: 30px;
  }
  #related-content-container {
    width: 100%;
    position: relative;
    top: auto;
    right: auto;
    margin-bottom: 50px;
  }
  .related-header {
    font-size: 105%;
  }
  #timeline-popout,
  #glossary-tool-popout,
  #dynasty-popout {
    max-height: 275px;
    top: auto;
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    z-index: 62;
    border: 2px solid $theme-medium-dark;
  }
  #timeline-popout-title,
  #glossary-tool-popout-title,
  #dynasty-popout-title {
    height: 50px;
  }
  #timeline-popout-scroll {
    max-height: 130px;
  }
  #glossary-tool-scroll {
    max-height: 100px;
  }
  #dynasty-popout-scroll {
    max-height: 200px;
  }
}

@media only screen and (max-width: 849px) {
  #timeline-popout-title,
  #glossary-tool-popout-title,
  #dynasty-popout-title {
    font-size: 105%;
  }
}

@media only screen and (max-width: 724px) {
  #photo-info-wrapper {
    flex-direction: column;
  }
  #photo-container {
    width: 100%;
    padding-right: 0;
    padding-bottom: 30px;
  }
  #info-container {
    width: 100%;
    padding-left: 0;
  }
  #timeline-popout,
  #glossary-tool-popout,
  #dynasty-popout {
    width: 450px;
  }
}

@media only screen and (max-width: 599px) {
  #database-object-container {
    padding: 0 30px;
  }
  #timeline-popout,
  #glossary-tool-popout,
  #dynasty-popout {
    width: 400px;
  }
}

@media only screen and (max-width: 499px) {
  .glossary-entry-container,
  #timeline-popout,
  #glossary-tool-popout,
  #dynasty-popout {
    width: 350px;
  }
}

@media only screen and (max-width: 399px) {
  .glossary-entry-container {
    width: 250px;
  }
  #timeline-popout,
  #glossary-tool-popout,
  #dynasty-popout {
    max-height: 300px;
    width: 250px;
  }
  #timeline-popout-option {
    height: auto;
    padding: 0 15px;
  }
  #timeline-scroll-subheader {
    height: auto;
    padding: 10px;
  }
  #timeline-scroll-text > div {
    padding: 10px;
  }
}

@media print {
  #database-object-wrapper,
  #photo-info-wrapper,
  #photo-container,
  #info-container {
    display: block;
    width: 100%;
  }
  #info-citation-link p,
  #info-citation-link a {
    color: black;
  }
  #languages,
  .glossary-entry-container,
  #back,
  #links-container,
  #related-content-container {
    display: none;
  }
}
</style>